'use client';

import { useTranslations } from 'next-intl';
import { ComponentProps, ReactNode, createContext, useContext } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { CookieSettingsValue, useCookieSettings } from '@/cookies';
import { EventService } from '@/shared/lib/event-service';
import { Button } from '@/shared/ui/button';
import { Form } from '@/shared/ui/form';
import { Layout } from '@/shared/ui/layout';
import { Modal } from '@/shared/ui/modal';
import { ModalProps } from '@/shared/ui/modal/types';
import { Switch } from '@/shared/ui/switch';
import './cookies-settings-modal.scss';

type FormValues = {
    analytical: boolean;
    marketing: boolean;
};

type CookiesSettingsModalContextValue = {
    onClose: ModalProps['onClose'];
    onSubmit: (data: FormValues) => void;
    form: ReturnType<typeof useForm<FormValues>>;
};

const CookiesSettingsModalContext = createContext<CookiesSettingsModalContextValue | null>(null);

const useCookiesSettingsModalContext = () => {
    const context = useContext(CookiesSettingsModalContext);
    if (!context) {
        throw new Error('CookiesSettingsModal components must be used within CookiesSettingsModalWrapper.');
    }
    return context;
};

type Props = ModalProps & {
    children?: ReactNode;
};

export const CookiesSettingsModalWrapper = ({ onClose, children, ...props }: Props) => {
    const cookiesSettings = useCookieSettings((s) => s.cookieSettings);

    const form = useForm<FormValues>({
        defaultValues: cookiesSettings.categories,
    });

    const handleSubmit = (values: FormValues) => {
        const nowISODate = new Date().toISOString();
        const data: CookieSettingsValue = {
            categories: values,
            consentTimestamp: cookiesSettings.consentTimestamp ?? nowISODate,
            lastConsentTimestamp: nowISODate,
        };
        EventService.trigger('cookies:update-consents', { data });
        onClose();
    };

    return (
        <CookiesSettingsModalContext.Provider value={{ onClose, onSubmit: handleSubmit, form }}>
            <Modal
                {...props}
                onClose={onClose}
                wrapperProps={{
                    className: 'cookies-settings-modal',
                    'data-testid': 'cookies-settings-modal',
                }}
                closeOnOverlayClick={false}
            >
                {children}
            </Modal>
        </CookiesSettingsModalContext.Provider>
    );
};

/** Provider that only provides context and renders children (Modal prefab). */
export const CookiesSettingsModalProvider = ({ onClose, children }: Props) => {
    const cookiesSettings = useCookieSettings((s) => s.cookieSettings);

    const form = useForm<FormValues>({
        defaultValues: cookiesSettings.categories,
    });

    const handleSubmit = (values: FormValues) => {
        const nowISODate = new Date().toISOString();
        const data: CookieSettingsValue = {
            categories: values,
            consentTimestamp: cookiesSettings.consentTimestamp ?? nowISODate,
            lastConsentTimestamp: nowISODate,
        };
        EventService.trigger('cookies:update-consents', { data });
        onClose();
    };

    return (
        <CookiesSettingsModalContext.Provider value={{ onClose, onSubmit: handleSubmit, form }}>
            {children}
        </CookiesSettingsModalContext.Provider>
    );
};

type FormProps = ComponentProps<typeof Form>;
const CookiesSettingsModalForm = ({ children, ...props }: FormProps) => {
    const { form, onSubmit } = useCookiesSettingsModalContext();
    return (
        <Form {...form} {...props} onSubmit={form.handleSubmit(onSubmit)}>
            {children}
        </Form>
    );
};

/** Renders only the title text. Use inside prefab: "Modal.Title". */
const CookiesSettingsModalTitle = () => {
    const t = useTranslations();
    return <>{t('COOKIES_SETTINGS_MODAL.TITLE')}</>;
};

type OptionTitleProps = ComponentProps<typeof Layout>;
const CookiesSettingsModalRecommendedTitle = ({ media, ...props }: OptionTitleProps) => {
    const t = useTranslations();
    return (
        <Layout element="b" media={media} data-testid="cookies-settings-modal-recommended-title" {...props}>
            {t('COOKIES_SETTINGS_MODAL.RECOMMENDED_TITLE')}
        </Layout>
    );
};

const CookiesSettingsModalTechnicalTitle = ({ media, ...props }: OptionTitleProps) => {
    const t = useTranslations();
    return (
        <Layout element="b" media={media} data-testid="cookies-settings-modal-technical-title" {...props}>
            {t('COOKIES_SETTINGS_MODAL.FUNCTIONAL_TITLE')}
        </Layout>
    );
};

const CookiesSettingsModalAnalyticalTitle = ({ media, ...props }: OptionTitleProps) => {
    const t = useTranslations();
    return (
        <Layout element="b" media={media} data-testid="cookies-settings-modal-analytical-title" {...props}>
            {t('COOKIES_SETTINGS_MODAL.ANALYTICAL_TITLE')}
        </Layout>
    );
};

const CookiesSettingsModalMarketingTitle = ({ media, ...props }: OptionTitleProps) => {
    const t = useTranslations();
    return (
        <Layout element="b" media={media} data-testid="cookies-settings-modal-marketing-title" {...props}>
            {t('COOKIES_SETTINGS_MODAL.MARKETING_TITLE')}
        </Layout>
    );
};

type OptionDescriptionProps = ComponentProps<typeof Layout>;
const CookiesSettingsModalRecommendedDescription = ({ media, ...props }: OptionDescriptionProps) => {
    const t = useTranslations();
    return (
        <Layout
            element="span"
            media={media}
            data-testid="cookies-settings-modal-recommended-description"
            {...props}
        >
            {t('COOKIES_SETTINGS_MODAL.RECOMMENDED_DESCRIPTION')}
        </Layout>
    );
};

const CookiesSettingsModalTechnicalDescription = ({ media, ...props }: OptionDescriptionProps) => {
    const t = useTranslations();
    return (
        <Layout
            element="span"
            media={media}
            data-testid="cookies-settings-modal-technical-description"
            {...props}
        >
            {t('COOKIES_SETTINGS_MODAL.FUNCTIONAL_DESCRIPTION')}
        </Layout>
    );
};

const CookiesSettingsModalAnalyticalDescription = ({ media, ...props }: OptionDescriptionProps) => {
    const t = useTranslations();
    return (
        <Layout
            element="span"
            media={media}
            data-testid="cookies-settings-modal-analytical-description"
            {...props}
        >
            {t('COOKIES_SETTINGS_MODAL.ANALYTICAL_DESCRIPTION')}
        </Layout>
    );
};

const CookiesSettingsModalMarketingDescription = ({ media, ...props }: OptionDescriptionProps) => {
    const t = useTranslations();
    return (
        <Layout
            element="span"
            media={media}
            data-testid="cookies-settings-modal-marketing-description"
            {...props}
        >
            {t('COOKIES_SETTINGS_MODAL.MARKETING_DESCRIPTION')}
        </Layout>
    );
};

const CookiesSettingsModalRecommendedSwitch = () => {
    const { form } = useCookiesSettingsModalContext();
    const isActive = useWatch({
        control: form.control,
        compute: (formValues) => formValues.marketing && formValues.analytical,
    });

    return (
        <Switch
            name="recommended"
            data-testid="cookies-settings-modal-recommended-switch"
            checked={isActive}
            onChange={(e) => {
                form.setValue('analytical', e.target.checked);
                form.setValue('marketing', e.target.checked);
            }}
        />
    );
};

const CookiesSettingsModalTechnicalSwitch = () => {
    return <Switch name="technical" data-testid="cookies-settings-modal-technical-switch" checked disabled />;
};

const CookiesSettingsModalAnalyticalSwitch = () => {
    return <Form.Switch name="analytical" data-testid="cookies-settings-modal-analytical-switch" />;
};

const CookiesSettingsModalMarketingSwitch = () => {
    return <Form.Switch name="marketing" data-testid="cookies-settings-modal-marketing-switch" />;
};

type SubmitButtonProps = ComponentProps<typeof Button>;
const CookiesSettingsModalSubmitButton = ({ ...props }: SubmitButtonProps) => {
    const t = useTranslations();
    return (
        <Layout element={Button} type="submit" data-testid="cookies-settings-modal-save-button" {...props}>
            {t('COOKIES_SETTINGS_MODAL.SAVE_BUTTON')}
        </Layout>
    );
};

export { CookiesSettingsModalWrapper as CookiesSettingsModal };

export {
    CookiesSettingsModalWrapper as Wrapper,
    CookiesSettingsModalProvider as Provider,
    CookiesSettingsModalForm as Form,
    CookiesSettingsModalTitle as Title,
    CookiesSettingsModalRecommendedTitle as RecommendedTitle,
    CookiesSettingsModalRecommendedDescription as RecommendedDescription,
    CookiesSettingsModalTechnicalTitle as TechnicalTitle,
    CookiesSettingsModalTechnicalDescription as TechnicalDescription,
    CookiesSettingsModalAnalyticalTitle as AnalyticalTitle,
    CookiesSettingsModalAnalyticalDescription as AnalyticalDescription,
    CookiesSettingsModalMarketingTitle as MarketingTitle,
    CookiesSettingsModalMarketingDescription as MarketingDescription,
    CookiesSettingsModalRecommendedSwitch as RecommendedSwitch,
    CookiesSettingsModalTechnicalSwitch as TechnicalSwitch,
    CookiesSettingsModalAnalyticalSwitch as AnalyticalSwitch,
    CookiesSettingsModalMarketingSwitch as MarketingSwitch,
    CookiesSettingsModalSubmitButton as SubmitButton,
};
